import { useContext } from 'react'
import { useLocation } from 'react-router-dom'

import PropTypes from 'prop-types'
import { Helmet } from 'react-helmet-async'

import { faBan, faCheck } from '@fortawesome/pro-light-svg-icons'

import {
  Box, Button, Columns, DashboardModule, LoadingModule,
} from '@domain-group/suit-theme'

import PageContext from '@contexts/pageContext'

import { generateUrls } from '@hooks/useBrand'
import useBrands from '@hooks/useBrands'

import ListBlankState from '@components/ListBlankState'
import PageFilterModule from '@components/PageFilterModule'
import PageHeader from '@components/PageHeader'

import { getQueryParamsFromHash, parsePermittedQueryParams } from '@domain-group/javascript-utils'

import BrandListItem from '../BrandListItem'

const buildPageFilterFields = () => {
  const style = {
    marginBottom: 16,
  }

  return [
    {
      component: 'DynamicInput',
      componentProps: {
        inputComponent: 'input',
        style,
      },
      key: 'string',
      label: 'Filter by Title or ID',
    },
  ]
}

interface PageContentProps {
  pageFilters: {
    [key: string]: any,
  },
}

const PageContent = (props: PageContentProps) => {
  const { pageFilters } = props

  // useLocation to refresh the window.location. If not used location won't reload if URL changes
  useLocation()

  const { hidden }: { hidden?: 'true' | 'false' } = parsePermittedQueryParams(getQueryParamsFromHash(), ['hidden'])
  const isHidden = hidden || 'false'

  const brandsPayload = useBrands({
    filters: {
      hidden: isHidden,
      ...pageFilters,
    },
    performHttpRequests: true,
  })

  const {
    callbacks: {
      loadMore,
    },
    canLoadMore,
    filteredBrands,
    filteredBrandsCount,
    hasFilteredBrands,
    loading,
  } = brandsPayload

  const pageContext = useContext(PageContext)
  const {
    callbacks: {
      resetFilters,
      showCreateBrandModal,
      updateFilters,
    },
    defaultFilters,
  } = pageContext

  const { brandsIndexUrl, hiddenBrandsIndexUrl } = generateUrls()

  return (
    <>
      <Helmet>
        <title>Brands | Realhub</title>
      </Helmet>

      <PageHeader
        actionContent={(
          <Button buttonStyle="primaryCreate" onClick={() => showCreateBrandModal()} size="medium">
            Create
          </Button>
        )}
        activeTabBarItemKey={isHidden}
        tabBarItems={[
          {
            href: brandsIndexUrl,
            icon: faCheck,
            key: 'false',
            title: 'Current',
          },
          {
            href: hiddenBrandsIndexUrl,
            icon: faBan,
            key: 'true',
            title: 'Archived',
          },
        ]}
        title="Brands"
      />

      <Box paddingX="large" paddingTop={[112, 105]}>
        <Columns boxProps={{ marginTop: 'large' }} flexDirection={['column', 'column', 'row']}>
          <Columns.Main>
            <LoadingModule loading={loading} times={3} />

            {!loading && !hasFilteredBrands && <ListBlankState />}

            {!loading && hasFilteredBrands && (
              <DashboardModule title="Brands">
                <Box flexDirection="column">
                  <Box flexDirection="column">
                    {filteredBrands.map(brand => (
                      <BrandListItem key={brand.id} brand={brand} />
                    ))}
                  </Box>

                  <DashboardModule.LoadMoreFooter
                    callbacks={{ loadMore }}
                    canLoadMore={canLoadMore}
                    entityCount={filteredBrandsCount}
                    loading={loading}
                  />
                </Box>
              </DashboardModule>
            )}
          </Columns.Main>

          <Columns.Sidebar>
            <PageFilterModule
              callbacks={{ resetFilters, updateFilters }}
              defaultFilters={defaultFilters}
              // @ts-expect-error TS2322
              filterFields={buildPageFilterFields()}
              pageFilters={pageFilters}
              title="Filters"
            />
          </Columns.Sidebar>
        </Columns>
      </Box>
    </>
  )
}

PageContent.propTypes = {
  pageFilters: PropTypes.object.isRequired,
}

export default PageContent
